import { formatDate, formatDate2 } from "./utils";

export const toDateOnlyString = (value) => {
  if (!value) return "";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    return value.slice(0, 10);
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const fetchPayPreview = async (http, code, numberMonths = 1) => {
  const response = await http.get(`payall/preview/${code}`, {
    params: { numberMonths },
  });
  return response.data.data;
};

export const buildPayDialogMessage = ({
  name,
  preview,
  gridPaymentDate,
  includeAdminNotes,
}) => {
  const live = toDateOnlyString(preview.currentPaymentDate);
  const next = toDateOnlyString(preview.previewPaymentDate);
  const grid = toDateOnlyString(gridPaymentDate);
  const liveLabel = formatDate(live) || live;
  const nextLabel = formatDate(next) || next;
  const gridLabel = formatDate(grid) || grid;

  const lines = [
    `Which payment date would you like to update for ${name}?`,
    "",
    `Current expiry (catalog, live): ${liveLabel}`,
    `After Payment or Both (+1 month): ${nextLabel}`,
  ];

  if (includeAdminNotes) {
    lines.push(
      `Subscr will set the tenant to the live catalog date (no months added): ${liveLabel}`
    );
  }

  if (grid && live && grid !== live) {
    lines.push("");
    lines.push(
      `The grid still shows ${gridLabel}. Someone or something updated this subscription while this page was open. The buttons will use the live catalog date, not the grid.`
    );
  }

  return lines.join("\n");
};

export const priceTypeArabic = (priceType) => {
  if (priceType === "Bronze") return "البرونزي";
  if (priceType === "Silver") return "الفضي";
  if (priceType === "Golden") return "الذهبي";
  if (priceType === "Platinum") return "البلاتيني";
  if (priceType === "Diamond") return "الماسي";
  return "";
};

export const liveExpiryFromPreview = (preview) =>
  toDateOnlyString(preview?.currentPaymentDate);

export const minusOneCalendarDay = (storedExpiry) => {
  const date = new Date(toDateOnlyString(storedExpiry));
  date.setDate(date.getDate() - 1);
  return date;
};

export const addCalendarDays = (storedExpiry, days) => {
  const date = new Date(toDateOnlyString(storedExpiry));
  date.setDate(date.getDate() + days);
  return date;
};

export const buildPaySuccessToast = ({
  writtenExpiry,
  previewExpiry,
  orderId,
  actionLabel = "Payment updated successfully",
}) => {
  const written = toDateOnlyString(writtenExpiry);
  const preview = toDateOnlyString(previewExpiry);
  let text = `${actionLabel}. Written expiry: ${written}`;
  if (orderId) {
    text += `. Order ID: ${orderId}`;
  }
  if (preview && written && preview !== written) {
    text += `. The dialog showed ${preview}; a payment or another write landed after the preview, so the server used the live catalog date instead.`;
  }
  return text;
};

export const buildRenewalClipboardText = ({
  name,
  priceType,
  paymentExpiration,
  capitalizeName,
}) => {
  const expiry = minusOneCalendarDay(paymentExpiration);
  const formattedDate = formatDate2(expiry);
  const capitalizedName = capitalizeName(name);
  const priceTypeString = priceTypeArabic(priceType);

  return `عزيزي العميل،
تم تجديد اشتراك الباقة ${priceTypeString} لنظام ${capitalizedName} بنجاح.

صالحة حتى تاريخ ${formattedDate}. نحن نقدر ثقتكم في خدماتنا.
        
في حال كان لديكم أي استفسارات، تحتاجون إلى مساعدة أو معلومات إضافية، فلا تترددوا في التواصل مع فريق الدعم لدينا.

نحن هنا دائمًا لضمان حصولكم على خدمات عالية الجودة وتلبية احتياجاتكم بكفاءة.
        
شكرًا لاستمراركم معنا كجزء من عالم OwlTech. نتطلع دائمًا لتقديم أفضل الخدمات لكم.
        
مع أطيب التحيات،  
فريق أوول تك | OwlTech  
https://www.owltecheg.com  
📞 +20 100 637 5557  
📧 info@owltecheg.com`;
};

export const datesFromPayResponse = (response) => {
  const payload = response?.data?.data || {};
  return {
    currentPaymentDate: toDateOnlyString(
      payload.currentPaymentDate || payload.subscribeUntill
    ),
    paymentExpiration: toDateOnlyString(
      payload.paymentExpiration || payload.subscribeUntill
    ),
    subscribeUntill: toDateOnlyString(payload.subscribeUntill),
    orderId: payload.orderId,
    numberMonths: payload.numberMonths,
  };
};

export const buildSmsRenewalText = ({ name, priceType, storedExpiry, capitalizeName }) => {
  const formattedDate = minusOneCalendarDay(storedExpiry).toISOString().split("T")[0];
  return `تم تجديد الباقة ${priceType} لنظام ${capitalizeName(name)} صالحة حتى ${formattedDate}`;
};

export const buildInfoCopyCardText = ({
  name,
  priceType,
  storedExpiry,
  price,
  capitalizeName,
}) => {
  const formattedDate = formatDate2(minusOneCalendarDay(storedExpiry));
  return `حضرتك مشترك على نظام: ${capitalizeName(name)}\nالباقة: ${priceTypeArabic(priceType)}\nقيمة الاشتراك: ${price || "غير محددة"} جنيه\nصالحة حتى: ${formattedDate}.`;
};

export const buildNoticeText = ({ name, priceType, storedExpiry, capitalizeName }) => {
  const date = new Date(toDateOnlyString(storedExpiry));
  date.setDate(date.getDate());
  const formattedDate = formatDate2(date);
  return `عزيزي العميل،  
              
نأمل أن تكونوا بأتم الصحة والعافية. نود تذكيركم بضرورة تجديد اشتراك الباقة ${priceTypeArabic(priceType)} قبل تاريخ ${formattedDate} لنظام ${capitalizeName(name)}، لضمان استمرار الاستفادة من خدماتنا المميزة.  

نحن هنا لمساعدتكم في إتمام عملية التجديد والإجابة على أي استفسارات لديكم. لا تترددوا في التواصل مع فريق خدمة العملاء للحصول على الدعم والمعلومات اللازمة.  

نحن نقدر ثقتكم في خدماتنا، ونتطلع دائمًا إلى تزويدكم بأفضل الحلول والتجربة المثلى.  

شكرًا لاختياركم أوول تك. نحن سعداء بأن نكون جزءًا من رحلتكم الرقمية.  

مع أطيب التحيات،  
فريق أوول تك | OwlTech  
https://www.owltecheg.com  
📞 +20 100 637 5557  
📧 info@owltecheg.com`;
};

export const buildLateNoticeText = ({ name, priceType, storedExpiry, capitalizeName }) => {
  const formattedDate = formatDate2(minusOneCalendarDay(storedExpiry));
  const formattedDate2 = formatDate2(addCalendarDays(storedExpiry, 5));
  return `عزيزي العميل،  
                      
نأمل أن تكونوا بأتم الصحة والعافية. نود أن نذكركم بأن صلاحية باقتكم ${priceTypeArabic(priceType)} قد انتهت في تاريخ ${formattedDate} لنظام ${capitalizeName(name)} . إذا كنتم ترغبون في الاستمرار والاستفادة من خدماتنا المميزة، نحن نهتم بتقديم أفضل الخدمات لكم.  
                      
يرجى التأكد من استمراركم في التواصل معنا قبل تاريخ ${formattedDate2} ، حتى لا يتم إلغاء تنشيط النظام وإنهاء التعاقد نهائيًا.  
                      
نحن هنا لمساعدتكم في تجديد الباقة والاستجابة لأي استفسارات قد تكون لديكم. يمكنكم التواصل مع فريق خدمة العملاء.  
                      
نشكركم على اختياركم خدماتنا، ونتطلع إلى استمرار تعاوننا.  
 
مع أطيب التحيات،  
فريق أوول تك | OwlTech  
https://www.owltecheg.com  
📞 +20 100 637 5557  
📧 info@owltecheg.com`;
};

export const buildLateText = ({ name, storedExpiry, capitalizeName }) => {
  const formattedDate = formatDate2(addCalendarDays(storedExpiry, 5));
  return `عزيزي العميل،  

نأسف لإعلامك بأنه سيتم إلغاء تنشيط خدمتك لنظام ${capitalizeName(name)} في ${formattedDate}، بسبب عدم تجديدها في الوقت المناسب. وفقًا لسياسة الشركة، سيتم إلغاء التعاقد نهائيًا.  
                            
إذا كنت ترغب في استعادة الخدمة، يُرجى التواصل معنا على الرقم 01006375557 للحصول على المساعدة.  
                            
نشكرك على اختيارك خدماتنا ونتطلع إلى خدمتك مجددًا في المستقبل.                        

مع أطيب التحيات،  
فريق أوول تك | OwlTech  
https://www.owltecheg.com  
📞 +20 100 637 5557  
📧 info@owltecheg.com`;
};
